import { useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, getToken } from '@/lib/api'
import { formatDateTime, money, moneyPlain, paymentLabel } from '@/lib/format'
import { toastError } from '@/store/toast'
import type { Receipt } from '@yui-receipt/shared'

type PdfVariant = 'both' | 'original' | 'copy'

export default function ReceiptDetailPage() {
  const { id } = useParams<{ id: string }>()
  const printIframeRef = useRef<HTMLIFrameElement | null>(null)
  const [printRequest, setPrintRequest] = useState<{ variant: 'original' | 'copy'; ts: number } | null>(null)

  const { data, isLoading, error } = useQuery<{ receipt: Receipt }>({
    queryKey: ['receipt', id],
    queryFn: () => api(`/receipts/${id}`),
    enabled: !!id,
  })

  function handlePrintVariant(variant: 'original' | 'copy') {
    setPrintRequest({ variant, ts: Date.now() })
  }

  function onPrintIframeLoad() {
    if (!printRequest) return
    const iframe = printIframeRef.current
    if (!iframe?.contentWindow) {
      toastError('ไม่สามารถสั่งพิมพ์ได้ — กดดาวน์โหลดแทน')
      setPrintRequest(null)
      return
    }
    try {
      iframe.contentWindow.focus()
      iframe.contentWindow.print()
    } catch (e) {
      toastError('ไม่สามารถสั่งพิมพ์ได้ — กดดาวน์โหลดแทน')
      // eslint-disable-next-line no-console
      console.error(e)
    } finally {
      setPrintRequest(null)
    }
  }

  if (isLoading) {
    return <p className="max-w-3xl mx-auto p-6 text-slate-500">กำลังโหลด…</p>
  }
  if (error || !data) {
    return (
      <p className="max-w-3xl mx-auto p-6 text-rose-600">
        โหลดใบเสร็จไม่สำเร็จ —{' '}
        <Link to="/receipts" className="underline">
          กลับไปรายการ
        </Link>
      </p>
    )
  }

  const r = data.receipt
  // Attach token + optional variant to the PDF URL so iframes can fetch
  // without cookies (token-in-query is allowed on GET only).
  const token = getToken()
  function buildPdfSrc(variant: PdfVariant, cacheBust?: number): string | null {
    if (!r.pdfUrl) return null
    const params = new URLSearchParams()
    if (variant !== 'both') params.set('variant', variant)
    if (token) params.set('token', token)
    if (cacheBust !== undefined) params.set('_', String(cacheBust))
    const qs = params.toString()
    return qs ? `${r.pdfUrl}${r.pdfUrl.includes('?') ? '&' : '?'}${qs}` : r.pdfUrl
  }
  const pdfSrc = buildPdfSrc('both')
  const printSrc = printRequest ? buildPdfSrc(printRequest.variant, printRequest.ts) : null

  return (
    <div className="max-w-5xl mx-auto p-4 grid grid-cols-1 lg:grid-cols-5 gap-4">
      <section className="card lg:col-span-2">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-lg font-semibold">{r.receiptNo ?? `#${r.id}`}</h1>
          {r.voidedAt && (
            <span className="text-xs px-2 py-1 rounded bg-rose-100 text-rose-700">
              VOID
            </span>
          )}
        </div>
        <dl className="space-y-1 text-sm">
          <div className="flex justify-between">
            <dt className="text-slate-500">วันที่</dt>
            <dd>{formatDateTime(r.createdAt)}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-slate-500">ผู้ออก</dt>
            <dd>{r.createdBy}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-slate-500">ชำระ</dt>
            <dd>{paymentLabel(r.paymentMethod)}</dd>
          </div>
        </dl>
        <div className="mt-3 pt-3 border-t border-slate-200">
          <div className="text-sm text-slate-500">คนไข้</div>
          {r.customer ? (
            <>
              <div className="font-medium">{r.customer.nameTh}</div>
              <div className="text-xs font-mono text-slate-500">
                {r.customer.cidFull ?? r.customer.cidMasked}
              </div>
            </>
          ) : r.customerNameSnapshot ? (
            <>
              <div className="font-medium">{r.customerNameSnapshot}</div>
              {r.customerPhoneSnapshot && (
                <div className="text-xs font-mono text-slate-500">
                  📞 {r.customerPhoneSnapshot}
                </div>
              )}
            </>
          ) : (
            <div className="text-slate-400">Walk-in</div>
          )}
        </div>
        <div className="mt-3 pt-3 border-t border-slate-200">
          <div className="text-sm text-slate-500 mb-1">รายการ</div>
          <table className="w-full text-sm">
            <tbody>
              {r.items.map((it) => (
                <tr key={it.id ?? it.name} className="border-b border-slate-100">
                  <td className="py-1">
                    {it.name}{' '}
                    <span className="text-slate-400">× {it.qty}</span>
                    {!it.vatable && (
                      <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-slate-100 text-slate-500">
                        ยกเว้น VAT
                      </span>
                    )}
                  </td>
                  <td className="py-1 text-right">{moneyPlain(it.subtotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <dl className="mt-2 text-sm space-y-1">
            {r.subtotalExempt > 0 && (
              <>
                <div className="flex justify-between">
                  <dt className="text-slate-500">ยอดคิดภาษี</dt>
                  <dd>{moneyPlain(r.subtotalVatable)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-slate-500">ยอดยกเว้นภาษี</dt>
                  <dd>{moneyPlain(r.subtotalExempt)}</dd>
                </div>
              </>
            )}
            <div className="flex justify-between">
              <dt className="text-slate-500">Subtotal</dt>
              <dd>{moneyPlain(r.subtotal)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">
                VAT {(r.vatRate * 100).toFixed(0)}%
              </dt>
              <dd>{moneyPlain(r.vat)}</dd>
            </div>
            {r.discount > 0 && (
              <div className="flex justify-between">
                <dt className="text-slate-500">ส่วนลด</dt>
                <dd className="text-rose-600">-{moneyPlain(r.discount)}</dd>
              </div>
            )}
            <div className="flex justify-between text-lg font-bold pt-1 border-t border-slate-200">
              <dt>รวม</dt>
              <dd>{money(r.total)}</dd>
            </div>
          </dl>
        </div>
        <div className="mt-4 pt-3 border-t border-slate-200 text-xs text-slate-500">
          <div className="font-medium text-slate-700">{r.issuer.name}</div>
          <div>
            เลขประจำตัวผู้เสียภาษี {r.issuer.taxId} {r.issuer.branch}
          </div>
          <div>{r.issuer.addressLine1}</div>
          <div>
            {r.issuer.addressLine2} Tel. {r.issuer.tel}
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2 no-print">
          <button
            className="btn-primary"
            onClick={() => handlePrintVariant('original')}
            disabled={!pdfSrc || !!printRequest}
          >
            🖨 พิมพ์ต้นฉบับ
          </button>
          <button
            className="btn-primary"
            onClick={() => handlePrintVariant('copy')}
            disabled={!pdfSrc || !!printRequest}
          >
            🖨 พิมพ์สำเนา
          </button>
          {pdfSrc && (
            <a
              className="btn-secondary col-span-2 text-center"
              href={pdfSrc}
              target="_blank"
              rel="noreferrer"
            >
              ⬇ ดาวน์โหลด PDF (ทั้งสองหน้า)
            </a>
          )}
        </div>
        <div className="mt-3">
          <Link to="/receipts" className="text-sm text-slate-500 hover:text-brand-600">
            ← กลับไปประวัติ
          </Link>
        </div>
      </section>

      <section className="card lg:col-span-3 p-0 overflow-hidden min-h-[600px]">
        {pdfSrc ? (
          <iframe
            title={`receipt-${r.receiptNo ?? r.id}`}
            src={pdfSrc}
            className="w-full h-[80vh] border-0"
          />
        ) : (
          <div className="p-6 text-slate-500 text-center">
            ยังไม่มี PDF — ใบเสร็จนี้อาจยังสร้างไม่เสร็จ
          </div>
        )}
      </section>

      {printSrc && (
        <iframe
          ref={printIframeRef}
          src={printSrc}
          onLoad={onPrintIframeLoad}
          title="print-target"
          className="hidden"
          aria-hidden
        />
      )}
    </div>
  )
}
